import { Utils, PCSS } from '@pacem/pacem-core';
import { PacemContenteditableHistoryCommandElement } from './historycommand';
import { PacemContenteditableAlignCommandElement } from './aligncommand';
import { PacemContenteditableLinkCommandElement } from './linkcommand';
import { PacemContenteditableImageCommandElement } from './imagecommand';
import { PacemContenteditableExecCommandElement, KnownExecCommand } from './execcommand';
//namespace Pacem.Components.Scaffolding {
function isRoot(element) {
    return element instanceof HTMLDivElement
        && element.contentEditable === 'true'
        && element.hasAttribute('pacem')
        && element.hasAttribute('role')
        && element.attributes['role'].value === 'presenter';
}
export class ContenteditableDOMObserver {
    constructor(contentElement, mutationCallback, selector = '*[contenteditable=false]') {
        this.#callback = mutationCallback;
        this.#selector = selector;
        this.#mutationObserver = new MutationObserver(list => {
            const fn = (item, removed) => {
                item.childNodes.forEach(i => /* recursion here */ fn(i, removed));
                mutationCallback(item, removed);
            };
            for (let entry of list) {
                const added = entry.addedNodes;
                const removed = entry.removedNodes;
                for (let j = 0; j < added.length; j++) {
                    const item = added.item(j);
                    fn(item, false);
                }
                for (let j = 0; j < removed.length; j++) {
                    const item = removed.item(j);
                    fn(item, true);
                }
            }
        });
        this._initContainer(contentElement);
    }
    _initContainer(contentElement) {
        contentElement.querySelectorAll(this.#selector).forEach(el => this.#callback(el));
        this.#mutationObserver.observe(contentElement, { subtree: true, childList: true });
        this.#contentElement = contentElement;
    }
    _disposeContainer(contentElement) {
        const observer = this.#mutationObserver;
        if (!Utils.isNull(observer)) {
            observer.disconnect();
        }
        contentElement.querySelectorAll(this.#selector).forEach(el => this.#callback(el, true));
    }
    dispose() {
        this._disposeContainer(this.#contentElement);
    }
    #selector;
    #callback;
    #contentElement;
    #mutationObserver;
}
export class ContenteditableUtils {
    static getDefaultDashboard() {
        const frag = new DocumentFragment();
        const toolbar0 = document.createElement('div');
        Utils.addClass(toolbar0, PCSS + '-contenteditable-toolbar');
        const undo = new PacemContenteditableHistoryCommandElement();
        undo.command = 'undo';
        undo.keyboardShortcut = "Ctrl+Z";
        const redo = new PacemContenteditableHistoryCommandElement();
        redo.command = 'redo';
        redo.keyboardShortcut = "Ctrl+Y";
        toolbar0.append(undo, redo);
        const separator0 = document.createElement('div');
        Utils.addClass(separator0, PCSS + '-contenteditable-separator');
        const toolbar1 = document.createElement('div');
        Utils.addClass(toolbar1, PCSS + '-contenteditable-toolbar');
        const bold = new PacemContenteditableExecCommandElement();
        bold.command = KnownExecCommand.Bold;
        bold.keyboardShortcut = "Ctrl+B";
        const italic = new PacemContenteditableExecCommandElement();
        italic.command = KnownExecCommand.Italic;
        italic.keyboardShortcut = "Ctrl+I";
        const underline = new PacemContenteditableExecCommandElement();
        underline.command = KnownExecCommand.Underline;
        underline.keyboardShortcut = "Ctrl+U";
        const strike = new PacemContenteditableExecCommandElement();
        strike.command = KnownExecCommand.StrikeThrough;
        underline.keyboardShortcut = "Ctrl+K";
        const ul = new PacemContenteditableExecCommandElement();
        ul.command = KnownExecCommand.UnorderedList;
        const ol = new PacemContenteditableExecCommandElement();
        ol.command = KnownExecCommand.OrderedList;
        const alignLeft = new PacemContenteditableAlignCommandElement();
        alignLeft.align = 'left';
        const alignCenter = new PacemContenteditableAlignCommandElement();
        alignCenter.align = 'center';
        const alignRight = new PacemContenteditableAlignCommandElement();
        alignRight.align = 'right';
        const justify = new PacemContenteditableAlignCommandElement();
        justify.align = 'justify';
        toolbar1.append(bold, italic, underline, strike, ul, ol, alignLeft, alignCenter, alignRight, justify);
        const separator1 = document.createElement('div');
        Utils.addClass(separator1, PCSS + '-contenteditable-separator');
        const toolbar2 = document.createElement('div');
        Utils.addClass(toolbar2, PCSS + '-contenteditable-toolbar');
        const link = new PacemContenteditableLinkCommandElement();
        const img = new PacemContenteditableImageCommandElement();
        toolbar2.append(link, img);
        frag.append(toolbar0, separator0, toolbar1, separator1, toolbar2);
        return frag;
    }
    static findSurroundingNode(range, arg1) {
        var node = range instanceof Node ? range : range.commonAncestorContainer;
        while (node && !(typeof arg1 === 'string' && node instanceof Element && node.tagName === arg1.toUpperCase()
            || typeof arg1 === 'function' && Utils.isNull(arg1.prototype) && arg1(node)
            || typeof arg1 === 'function' && !Utils.isNull(arg1.prototype) && node instanceof arg1)) {
            const parent = node.parentNode;
            if (isRoot(parent)) {
                node = null;
                break;
            }
            node = parent;
        }
        return node;
    }
    static findSurroundingBlockElement(range) {
        return this.findSurroundingNode(range, ContenteditableUtils.isBlockElement);
    }
    static findSurroundingSiblingBlockElements(range) {
        let output = [];
        if (isRoot(range.startContainer)) {
            if (!range.collapsed) {
                const root = range.startContainer;
                for (let k = range.startOffset; k <= range.endOffset; k++) {
                    const child = root.children.item(k);
                    if (this.isBlockElement(child)) {
                        output.push(root.children.item(k));
                    }
                }
            }
            return output;
        }
        // simil-collapsed
        if (range.startContainer === range.endContainer) {
            let retval = range.startContainer;
            while (!ContenteditableUtils.isBlockElement(retval) && retval.parentElement.contentEditable != 'true') {
                retval = retval.parentElement;
            }
            return retval instanceof Element ? [retval] :
                (retval.parentElement.contentEditable === 'true' ? [] : [retval.parentElement]);
        }
        // generic
        let root = range.commonAncestorContainer, started = false;
        for (let j = 0; j < root.childNodes.length; j++) {
            const iNode = root.childNodes.item(j);
            const pick = (!started && iNode.contains(range.startContainer))
                || started;
            if (pick && ContenteditableUtils.isBlockElement(iNode)) {
                output.push(iNode);
                started = true;
                if (iNode.contains(range.endContainer)) {
                    break;
                }
            }
        }
        return started ? output : (root instanceof Element && !isRoot(root) ? [root] : []);
    }
    static findContainingRootElements(range) {
        const range1 = range.cloneRange(), range2 = range.cloneRange();
        range1.collapse(true);
        range2.collapse();
        let root = this.findSurroundingNode(range1, n => n.parentElement.contentEditable === 'true');
        const retval = [root];
        while (!root.contains(range2.startContainer)) {
            retval.push(root = root.nextElementSibling);
        }
        return retval;
    }
    static isBlockElement(node) {
        return node instanceof Element && !getComputedStyle(node).display.startsWith('inline');
    }
    static select(node, inside = false) {
        const range = document.createRange();
        if (!inside) {
            range.setStartBefore(node);
            range.setEndAfter(node);
        }
        else {
            range.setStart(node, 0);
            range.setEnd(node, node.childNodes.length);
        }
        const selection = document.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        return range;
    }
    static copyAttributes(to, from) {
        const attrs = from.attributes;
        for (let j = 0; j < attrs.length; j++) {
            const attr = attrs.item(j);
            to.setAttribute(attr.name, attr.value);
        }
    }
    /**
     * Checks whether the provided range wraps an 'inert' (contentEditable=false) element, and returns result accordingly.
     * @param range
     */
    static testInertElementWrapping(range) {
        if (!Utils.isNull(range)) {
            const parent = range.startContainer, index = range.startOffset;
            var node;
            if (parent === range.endContainer && index == (range.endOffset - 1) && (node = parent.childNodes.item(index)) instanceof HTMLElement && node.contentEditable === 'false') {
                return { result: true, element: node };
            }
        }
        return { result: false };
    }
}
